import { Component, OnInit, Output, EventEmitter, Input, forwardRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BehaviorSubject, Observable } from 'rxjs';

import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { RSSFeedService } from '../../services/rss-feed.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { RSSFeedManageDialogComponent } from '../RSSFeedManageDialog/rss-feed-manage-dialog.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { BriefingFeedModel } from '../../../Briefings/models/briefing-feed.model';
import { MultiSelectDropdownComponent } from '@Common/Forms/components/MultiselectDropdown/multi-select-dropdown.component';
import { RSSFeed } from '@Monitor/Shared/models/rss-feed.model';

@Component({
    selector: 'agpr-rss-feed-picker',
    templateUrl: './rss-feed-picker.component.html',
    styleUrls: ['./rss-feed-picker.component.scss'],
    providers: [
        {
            provide: 'RSSFeedService',
            useClass: RSSFeedService
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RSSFeedPickerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class RSSFeedPickerComponent implements OnInit, ControlValueAccessor {

    @Input() id: string;
    @Input() value: BriefingFeedModel[] = [];
    @Input() isRssFeedMissing: boolean = false;

    @Output() change: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild(MultiSelectDropdownComponent) dropdown: MultiSelectDropdownComponent;

    feeds: RSSFeed[] = [];

    options: DropdownOptionModel[] = [];
    _onChange: Function = this.defaultOnChange;
    selectedFeeds;
    _onTouched: Function;

    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        private rssFeedService: RSSFeedService,
        private coreDialogService: CoreDialogService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.rssFeedService.all$.subscribe(
            feeds => {
                if (!feeds) {
                    return this.options = [];
                }
                this.feeds = feeds;

                // this is in case we delete a selected feed, so we need to delete it from selected list
                const filteredValue = this.value.filter(selected =>
                    feeds.some(feed => feed.id === selected.id)
                );
                if (filteredValue.length !== this.value.length ||
                      !filteredValue.every((item, i) => item.id === this.value[i].id)) {
                    const removedIndex = this.value.findIndex(selected =>
                        !feeds.some(f => f.id === selected.id)
                    );
                    if (removedIndex !== -1) {
                        this.removeFromSelectedList(removedIndex);
                        this.value = filteredValue;
                        this._onChange(this.value);
                        this.change.emit(this.value);
                    }
                }

                this.options = feeds.map(feed => {
                    const briefingFeed = new BriefingFeedModel();
                    briefingFeed.id = feed.id;
                    briefingFeed.setNameAndUrl(feed.name, feed.url);

                    return {
                        label: feed.name,
                        value: briefingFeed
                    };
                });
                this.selectedFeeds = this.value.map(feed => feed.id);
                this.updateValues();
            }
        );
    }


    fetchFeed(): Observable<RSSFeed[]> {
        return this.rssFeedService.all$;
    }

    writeValue(feeds: BriefingFeedModel[]): void {
        if (!feeds) {
            return;
        }
        this.value = feeds;
        this.selectedFeeds = this.value.map(feed => feed.id);
        this.updateValues();
        this.cd.markForCheck();
    }

    updateValues(): void {
        if (!this.value.length || !this.feeds.length) {
            return;
        }
        for (const feed of this.value) {
            if (feed.name) {
                continue;
            }
            feed.name = this.feeds.find(feedModel => feedModel.id === feed.id)?.name;
            feed.url = this.feeds.find(feedModel => feedModel.id === feed.id)?.url;
        }
    }

    defaultOnChange() {};

    registerOnChange(onChange: Function) {
        this._onChange = onChange;
    }
    registerOnTouched(onTouched: Function) {
        this._onTouched = onTouched;
    }

    selectedFeedsChanged(): void {
        if (!this.options.length) {
            return;
        }
        if (this.selectedFeeds.length < this.value.length) {
            this.value = this.value.filter(val => this.selectedFeeds.includes(val.id));
        } else {
            const newItemIndex = this.selectedFeeds.length - 1;
            const selectedIndex = this.options.findIndex(item => item.value.id === this.selectedFeeds[newItemIndex]);
            if (selectedIndex !== -1) {
                const value = this.options[selectedIndex].value;
                if (!this.value?.find(x => x.id === value.id)) {
                    this.value.push(value);
                }
            }
        }

        this._onChange(this.value);
        this.change.emit(this.value);
    }

    removeFromSelectedList(index): void {
        this.dropdown.removeItem({ value: this.value[index].id });
        this._onChange(this.value);
        this.change.emit(this.value);
    }

    showRSSFeedManageDialog(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(RSSFeedManageDialogComponent, io);
    }

    clearOnPublishChanged(): void {
        this.change.emit(this.value);
    }
}
